import { defineStore } from '@repo/stores';
import type { FabricDoc, MetadataProperty, MetaInfo, Palette } from '@repo/style3d';
import { computed, ref, toRaw, watch } from 'vue';
import { rgbToHex, setWindowTitle } from '@repo/utils';
import { getAppConfig } from '@/utils';
import { useRecentFabricListStore } from '@/store/recent-fabric-list.ts';
import { useParamStore } from '@/store/param.ts';

const { DEFAULT_COMPANY_NAME } = getAppConfig();

export interface FabricInfo {
  id: string;
  originUrl?: string;
  fabricId: string;
  info: MetaInfo;
  thumb: string;
  xhr_path?: string;
  enterpriseId: string;
  enterpriseName: string;
  resourceType?: 'cloudknit' | 'style3d';
  properties: Record<string, string>[];
  styleProperties?: Record<string, string>[];
  carousels?: (FabricDoc & { url: string })[];
  logo?: string;
  mask: {
    srcImage?:string;
    colors?:string[];
    list: string[];
  };
}

export interface ColorPalette extends Omit<Palette, 'color'> {
  color: string;
}

export const useFabricStore = defineStore('fabric', () => {
  const fabricInfo = ref<FabricInfo>();
  const recentFabricStore = useRecentFabricListStore();
  const paramStore = useParamStore();

  const fabricProperty = computed(() => {
    return fabricInfo.value?.properties || [];
  });

  const styleProperty = computed(() => {
    return fabricInfo.value?.styleProperties || [];
  });

  const palette = computed<ColorPalette[] | undefined>(() => {
    return fabricInfo.value?.info?.palette?.map(item => ({
      ...item,
      color: rgbToHex(item.color.R, item.color.G, item.color.B)
    }));
  });

  const setFabricInfo = async (val: FabricInfo) => {
    fabricInfo.value = val;

    const {
      id,
      fabricId,
      originUrl,
      thumb,
      enterpriseId,
      enterpriseName,
      info,
      resourceType = 'cloudknit',
      properties,
      mask
    } = val;

    await recentFabricStore.addRecentFabric({
      id,
      fabricId,
      originUrl,
      resourceType,
      info: toRaw(info),
      thumb: thumb || '',
      enterpriseId,
      enterpriseName,
      properties: toRaw(properties || []),
      mask
    });
  };

  const setFabricProperties = (val: MetadataProperty['data']) => {
    if (fabricInfo.value) {
      fabricInfo.value.properties = [...val.map(toRaw)];
    }
  };

  const setFabricThumb = (url: string) => {
    if (fabricInfo.value) {
      fabricInfo.value.thumb = url;
    }
  };

  watch(() => fabricInfo.value?.enterpriseName, (val) => {
    setWindowTitle(val || paramStore.dispalyname || DEFAULT_COMPANY_NAME);
  });

  // 选中的区域颜色索引（用于多区域变色）
  const selectedRegionIndex = ref<number>(0);

  const setSelectedRegionIndex = (index: number) => {
    selectedRegionIndex.value = index;
  };

  const updateRegionColor = (index: number, color: string) => {
    if (fabricInfo.value?.mask?.colors && index >= 0 && index < fabricInfo.value.mask.colors.length) {
      fabricInfo.value.mask.colors[index] = color;
    }
  };

  return { fabricInfo, fabricProperty, styleProperty, setFabricInfo, setFabricProperties, setFabricThumb, palette, selectedRegionIndex, setSelectedRegionIndex, updateRegionColor };
});
